import { useState } from "react"
import { Button } from "@aix/ui"
import { trpc } from "../../../lib/trpc"
import toast from "react-hot-toast"
import { useNavigate } from "@tanstack/react-router"

interface ResetPlanModalProps {
  coachId: string
  coachName: string
  currentWeek: number
  onClose: () => void
}

type ResetStep = "choose" | "quick-questions" | "confirm"
type ResetType = "quick" | "full" | "adjust" | null

export function ResetPlanModal({ coachId, coachName, currentWeek, onClose }: ResetPlanModalProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState<ResetStep>("choose")
  const [resetType, setResetType] = useState<ResetType>(null)
  const [quickAnswers, setQuickAnswers] = useState<{
    fitnessLevel?: "worse" | "same" | "better"
    timeAvailability?: "less" | "same" | "more"
    reasonForReset?: string
  }>({})

  const resetMutation = trpc.plans.quarterlyPlans.reset.useMutation({
    onSuccess: (data) => {
      toast.success(data.message)
      onClose()

      // Navigate based on reset type — both branches go to the same destination
      if (data.requiresNewIntake || data.newPlanId) {
        navigate({ to: "/coach-os/coaches/$coachId", params: { coachId } })
      }
    },
    onError: (error) => {
      toast.error(error.message || "Failed to reset plan")
    },
  })

  const handleQuickRestart = () => {
    resetMutation.mutate({
      coachId,
      resetType: "quick",
      currentState: quickAnswers,
    })
  }

  const handleFullReset = () => {
    resetMutation.mutate({
      coachId,
      resetType: "full",
    })
  }

  const handleAdjust = () => {
    resetMutation.mutate({
      coachId,
      resetType: "adjust",
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border-2 border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {step === "choose" && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">Reset Your Plan</h2>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                ✕
              </button>
            </div>

            <p className="text-muted-foreground">
              You&apos;re on Week {currentWeek} of 13 for <strong>{coachName}</strong>. Your current plan will be
              archived.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setResetType("quick")
                  setStep("quick-questions")
                }}
                className="w-full p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-secondary transition-all text-left group"
              >
                <div className="font-semibold text-foreground group-hover:text-primary">⚡ Quick Restart</div>
                <div className="text-sm text-muted-foreground">Keep goals, reset timeline (~2 min)</div>
              </button>

              <button
                onClick={() => {
                  setResetType("full")
                  setStep("confirm")
                }}
                className="w-full p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-secondary transition-all text-left group"
              >
                <div className="font-semibold text-foreground group-hover:text-primary">🔄 Full Reset</div>
                <div className="text-sm text-muted-foreground">New goals, new plan (~10 min)</div>
              </button>

              <button
                onClick={() => {
                  setResetType("adjust")
                  setStep("confirm")
                }}
                className="w-full p-4 border-2 border-border rounded-lg hover:border-primary hover:bg-secondary transition-all text-left group"
              >
                <div className="font-semibold text-foreground group-hover:text-primary">⚙️ Adjust Plan</div>
                <div className="text-sm text-muted-foreground">Modify weekly structure (~5 min)</div>
              </button>
            </div>

            <div className="flex justify-end pt-4 border-t border-border">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {step === "quick-questions" && (
          <div className="p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">Where are you at right now?</h3>
            <p className="text-muted-foreground">Quick check-in to adjust your baseline:</p>

            <div className="space-y-4">
              <div>
                <label className="font-medium text-foreground block mb-2">Fitness level compared to Week 1:</label>
                <div className="flex gap-2">
                  {(["worse", "same", "better"] as const).map((level) => (
                    <button
                      key={level}
                      onClick={() => setQuickAnswers({ ...quickAnswers, fitnessLevel: level })}
                      className={`flex-1 p-3 border-2 rounded-lg transition-all capitalize ${
                        quickAnswers.fitnessLevel === level
                          ? "border-primary bg-primary/10 text-primary font-semibold"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-medium text-foreground block mb-2">Time availability:</label>
                <div className="flex gap-2">
                  {(["less", "same", "more"] as const).map((time) => (
                    <button
                      key={time}
                      onClick={() => setQuickAnswers({ ...quickAnswers, timeAvailability: time })}
                      className={`flex-1 p-3 border-2 rounded-lg transition-all capitalize ${
                        quickAnswers.timeAvailability === time
                          ? "border-primary bg-primary/10 text-primary font-semibold"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-medium text-foreground block mb-2">What caused the break? (optional)</label>
                <textarea
                  placeholder="Life happened, got busy, lost motivation..."
                  className="w-full p-3 border-2 border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-hidden"
                  rows={3}
                  value={quickAnswers.reasonForReset || ""}
                  onChange={(e) => setQuickAnswers({ ...quickAnswers, reasonForReset: e.target.value })}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setStep("choose")}>
                Back
              </Button>
              <Button onClick={handleQuickRestart} loading={resetMutation.isLoading} className="flex-1">
                Generate New Plan →
              </Button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="p-6 space-y-4">
            <h3 className="text-xl font-bold text-foreground">Confirm Reset</h3>

            {resetType === "full" && (
              <>
                <p className="text-muted-foreground">This will start from scratch. Your old goals will be archived.</p>
                <div className="bg-secondary border-2 border-border rounded-lg p-4">
                  <p className="font-semibold text-foreground mb-2">What happens next:</p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>✓ Current plan archived as &quot;Week 1-{currentWeek} (archived)&quot;</li>
                    <li>✓ New intake conversation created</li>
                    <li>✓ Complete 5 intake questions</li>
                    <li>✓ New 13-week plan generated</li>
                  </ul>
                </div>
              </>
            )}

            {resetType === "adjust" && (
              <>
                <p className="text-muted-foreground">This will open adjustment mode for your current plan.</p>
                <div className="bg-secondary border-2 border-border rounded-lg p-4">
                  <p className="font-semibold text-foreground mb-2">What you can adjust:</p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>✓ Weekly plan structure</li>
                    <li>✓ Phase strategy</li>
                    <li>✓ Minor goal tweaks</li>
                    <li>✗ Major goal changes (use Full Reset for that)</li>
                  </ul>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setStep("choose")}>
                Back
              </Button>
              <Button
                onClick={resetType === "full" ? handleFullReset : handleAdjust}
                loading={resetMutation.isLoading}
                className="flex-1"
              >
                {resetType === "full" ? "Start Full Reset" : "Enable Adjust Mode"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
